import { useState, useMemo } from 'react';
import {
  FileBarChart2,
  FileText,
  Table2,
  Loader2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PrismaGenerator } from '@/features/export/components/PrismaGenerator';
import { ReportBuilder } from '@/features/export/components/ReportBuilder';
import { ExtractionTable } from '@/features/evaluation/components/ExtractionTable';
import { DataExtractionRunner } from '@/features/evaluation/components/DataExtractionRunner';
import { useExtractions } from '@/features/export/hooks/useExport';
import type { ExtractionField } from '@/features/export/hooks/useExport';

// =========================================
// Reports page — PRISMA + Report Builder + Extraction
// =========================================

export function Reports() {
  const [activeTab, setActiveTab] = useState('prisma');
  const { data: extractions, isLoading: extractionsLoading } = useExtractions();

  // Derive a unified schema from all extractions
  const extractionSchema = useMemo((): ExtractionField[] => {
    if (!extractions?.length) return [];

    // Merge schemas from all extractions (first one with schema wins per field)
    const fieldMap = new Map<string, ExtractionField>();
    for (const ext of extractions) {
      if (ext.extraction_schema) {
        for (const field of ext.extraction_schema) {
          if (!fieldMap.has(field.name)) {
            fieldMap.set(field.name, field);
          }
        }
      }
    }
    return Array.from(fieldMap.values());
  }, [extractions]);

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
        <p className="text-sm text-slate-500 mt-1">
          Generate PRISMA flow diagrams, automated review reports, and data extraction tables
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="prisma" className="gap-1.5">
            <FileBarChart2 className="h-3.5 w-3.5" />
            PRISMA Flow
          </TabsTrigger>
          <TabsTrigger value="report" className="gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            Report
          </TabsTrigger>
          <TabsTrigger value="extraction" className="gap-1.5">
            <Table2 className="h-3.5 w-3.5" />
            Extraction
          </TabsTrigger>
        </TabsList>

        {/* PRISMA Flow Diagram */}
        <TabsContent value="prisma" className="mt-6">
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-semibold text-slate-800">
                PRISMA 2020 Flow Diagram
              </h2>
              <Badge variant="outline" className="text-xs">
                Interactive
              </Badge>
            </div>
            <p className="text-sm text-slate-500">
              Fill in your systematic review numbers to generate a PRISMA 2020 compliant flow diagram. Export as SVG or PNG.
            </p>
          </div>
          <PrismaGenerator />
        </TabsContent>

        {/* Automated Report */}
        <TabsContent value="report" className="mt-6">
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-semibold text-slate-800">
                Review Report Generator
              </h2>
              <Badge variant="outline" className="text-xs">
                AI-Powered
              </Badge>
            </div>
            <p className="text-sm text-slate-500">
              Generate an automated systematic review report from your evaluated sources. Pick sections, customize instructions, and export as Markdown.
            </p>
          </div>
          <ReportBuilder />
        </TabsContent>

        {/* Data Extraction */}
        <TabsContent value="extraction" className="mt-6">
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-semibold text-slate-800">
                Data Extraction Table
              </h2>
              <Badge variant="outline" className="text-xs">
                Editable
              </Badge>
            </div>
            <p className="text-sm text-slate-500">
              View and edit structured data extracted from your sources. Export to CSV or Excel.
            </p>
          </div>

          {extractionsLoading ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <Loader2 className="h-8 w-8 animate-spin mb-3" />
              <p className="text-sm">Loading extractions...</p>
            </div>
          ) : (
            <>
              {extractions && extractions.length > 0 && (
                <ExtractionTable
                  extractions={extractions}
                  schema={extractionSchema}
                  className="mb-6"
                />
              )}
              <DataExtractionRunner />
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
